/**
 * Zona horaria del restaurante (13 oct 2026).
 *
 * Muchas altas antiguas no guardaron `tenant.timezone` (63 de México, 31 de
 * España…) y el servidor calculaba su «hoy» en UTC: a las 19:00 en México ya
 * era mañana. Mientras no tengan zona, se usa la principal de su país; la web
 * además manda una vez la del navegador (mejor: México tiene varias) y la API
 * la guarda solo si falta.
 *
 * Este mapa SOLO dice qué hora es en el país. No sirve para saber si un país
 * es europeo ni para decidir a quién se le manda algo.
 */
export const COUNTRY_TIMEZONE: Record<string, string> = {
  // Europa
  ES: 'Europe/Madrid', PT: 'Europe/Lisbon', AD: 'Europe/Andorra', FR: 'Europe/Paris', IT: 'Europe/Rome',
  DE: 'Europe/Berlin', GB: 'Europe/London', IE: 'Europe/Dublin', NL: 'Europe/Amsterdam', BE: 'Europe/Brussels',
  CH: 'Europe/Zurich', AT: 'Europe/Vienna',
  // Norteamérica
  US: 'America/New_York', CA: 'America/Toronto', MX: 'America/Mexico_City',
  // Centroamérica y Caribe
  GT: 'America/Guatemala', SV: 'America/El_Salvador', HN: 'America/Tegucigalpa', NI: 'America/Managua',
  CR: 'America/Costa_Rica', PA: 'America/Panama', BZ: 'America/Belize', CU: 'America/Havana',
  DO: 'America/Santo_Domingo', PR: 'America/Puerto_Rico',
  // Sudamérica
  CO: 'America/Bogota', VE: 'America/Caracas', EC: 'America/Guayaquil', PE: 'America/Lima',
  BO: 'America/La_Paz', CL: 'America/Santiago', AR: 'America/Argentina/Buenos_Aires', UY: 'America/Montevideo',
  PY: 'America/Asuncion', BR: 'America/Sao_Paulo',
};

/** ¿Es una zona IANA que entienden Node y el navegador? */
export function esZonaHorariaValida(zona: unknown): zona is string {
  if (typeof zona !== 'string' || !zona || zona.length > 80) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: zona });
    return true;
  } catch {
    return false;
  }
}

/**
 * ¿Vale la zona que da el navegador para guardarla sola? «UTC» o «Etc/…»
 * suele ser un equipo mal configurado: mejor quedarse con la del país.
 */
export function zonaDelNavegadorAprovechable(zona: unknown): zona is string {
  return esZonaHorariaValida(zona) && zona.includes('/') && !/^(Etc|UTC|GMT)\b/i.test(zona);
}

/** La zona con la que se calcula el «hoy» del restaurante: la suya, la de su país o UTC. */
export function zonaHorariaDelNegocio(tenant: { timezone?: string; country?: string } | null | undefined): string {
  if (esZonaHorariaValida(tenant?.timezone)) return tenant!.timezone!;
  return COUNTRY_TIMEZONE[tenant?.country ?? ''] ?? 'UTC';
}
